import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Checkbox,
  Steps,
  Typography,
  Space,
  Alert,
  Divider
} from "antd";
import { apiRequest, useAccount } from "@vojomo-core/shared";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface PlatformUser {
  _id: string;
  name: string;
  email: string;
}

const MODULE_TYPES = [
  { label: "QBR Scoring", value: "qbr_scoring" },
  { label: "Roadmap Builder", value: "roadmap_builder" },
  { label: "Topic Document", value: "topic_doc" }
];

export function CoordinatorCreate(): React.ReactElement {
  const navigate = useNavigate();
  useAccount();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noDeadline, setNoDeadline] = useState(false);

  useEffect(() => {
    apiRequest<PlatformUser[]>({
      subRoute: "company_admin",
      path: "/users"
    }).then(setUsers);
  }, []);

  const steps = [
    { title: "Details" },
    { title: "Audience" },
    { title: "Message" },
    { title: "Review" }
  ];

  const handleNext = async () => {
    try {
      const fields: string[][] = [
        ["name", "module_type"],
        ["audience_user_ids"],
        ["subject", "body"],
        []
      ];
      await form.validateFields(fields[currentStep]);
      setCurrentStep((s) => s + 1);
    } catch {
      // validation errors shown by form
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const values = form.getFieldsValue(true);
      const body = {
        name: values.name,
        module_type: values.module_type,
        assignment_url: values.assignment_url || "",
        audience_user_ids: values.audience_user_ids || [],
        channels: values.channels || ["email", "in_app"],
        subject: values.subject,
        body: values.body,
        deadline: noDeadline ? null : values.deadline?.toISOString() ?? null,
        no_deadline: noDeadline,
        lockdown_enabled: !noDeadline
      };

      const res = await apiRequest<{ workflow_id: string; message: string }>({
        method: "POST",
        subRoute: "workflow",
        path: "/create",
        body
      });

      navigate(`/coordinator/workflows/${res.workflow_id}`);
    } catch {
      setError("Failed to create workflow. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const values = form.getFieldsValue(true);
  const selectedUserNames = (values.audience_user_ids || [])
    .map((uid: string) => users.find((u) => u._id === uid)?.name ?? uid)
    .join(", ");

  return (
    <div style={{ padding: "24px 0", maxWidth: 680 }}>
      <Space style={{ marginBottom: 24, width: "100%", justifyContent: "space-between" }}>
        <Title level={4} style={{ margin: 0 }}>
          New Workflow
        </Title>
        <Button onClick={() => navigate("/coordinator/workflows")}>Cancel</Button>
      </Space>

      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 32 }}
        size="small"
      />

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          channels: ["email", "in_app"],
          lockdown_enabled: true
        }}
      >
        {/* Step 0: Details */}
        {currentStep === 0 && (
          <>
            <Form.Item
              name="name"
              label="Workflow name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="e.g. Q2 QBR Scoring" />
            </Form.Item>

            <Form.Item
              name="module_type"
              label="Platform module"
              rules={[{ required: true, message: "Select a module" }]}
            >
              <Select options={MODULE_TYPES} placeholder="Select a module" />
            </Form.Item>

            <Form.Item name="assignment_url" label="Assignment URL (optional)">
              <Input placeholder="https://app.vojomo.com/..." />
            </Form.Item>

            <Divider />

            <Form.Item label="Deadline">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Checkbox
                  checked={noDeadline}
                  onChange={(e) => setNoDeadline(e.target.checked)}
                >
                  No deadline
                </Checkbox>
                {!noDeadline && (
                  <Form.Item name="deadline" noStyle>
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(d) => d && d < dayjs().startOf("day")}
                    />
                  </Form.Item>
                )}
              </Space>
            </Form.Item>
          </>
        )}

        {/* Step 1: Audience */}
        {currentStep === 1 && (
          <>
            <Form.Item
              name="audience_user_ids"
              label="Select participants"
              rules={[{ required: true, message: "Select at least one participant" }]}
            >
              <Select
                mode="multiple"
                placeholder="Search by name or email…"
                optionFilterProp="label"
                options={users.map((u) => ({
                  label: `${u.name} (${u.email})`,
                  value: u._id
                }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        )}

        {/* Step 2: Message */}
        {currentStep === 2 && (
          <>
            <Form.Item
              name="subject"
              label="Email subject"
              rules={[{ required: true, message: "Subject is required" }]}
            >
              <Input placeholder="e.g. Action required: Q2 QBR Scoring" />
            </Form.Item>

            <Form.Item
              name="body"
              label="Message body"
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  You can use {"{First Name}"}, {"{Deadline}"}, and{" "}
                  {"{Assignment Link}"} as dynamic tokens.
                </Text>
              }
              rules={[{ required: true, message: "Body is required" }]}
            >
              <TextArea
                rows={6}
                placeholder={`Hi {First Name},\n\nPlease complete your QBR scoring by {Deadline}.\n\n{Assignment Link}`}
              />
            </Form.Item>

            <Form.Item name="channels" label="Delivery channels">
              <Checkbox.Group
                options={[
                  { label: "In-app notification", value: "in_app" },
                  { label: "Email", value: "email" }
                ]}
              />
            </Form.Item>
          </>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div style={{ lineHeight: 2 }}>
            <Title level={5}>Review before creating</Title>
            <Text strong>Name:</Text> {values.name}
            <br />
            <Text strong>Module:</Text>{" "}
            {MODULE_TYPES.find((m) => m.value === values.module_type)?.label ??
              values.module_type}
            <br />
            <Text strong>Participants:</Text>{" "}
            {(values.audience_user_ids || []).length} selected
            {selectedUserNames && (
              <Text type="secondary"> ({selectedUserNames})</Text>
            )}
            <br />
            <Text strong>Deadline:</Text>{" "}
            {noDeadline
              ? "None"
              : values.deadline
                ? dayjs(values.deadline).format("MMMM D, YYYY")
                : "Not set"}
            <br />
            <Text strong>Subject:</Text> {values.subject}
            <br />
            <Text strong>Channels:</Text>{" "}
            {(values.channels || []).join(", ")}
            <br />
            <Divider />
            <Text type="secondary">
              The workflow will be saved as a <b>draft</b>. You can review it
              and send it from the workflow detail page.
            </Text>
          </div>
        )}
      </Form>

      <div style={{ marginTop: 24, display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {currentStep > 0 && (
          <Button onClick={() => setCurrentStep((s) => s - 1)}>Back</Button>
        )}
        {currentStep < steps.length - 1 && (
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        {currentStep === steps.length - 1 && (
          <Button type="primary" loading={isSubmitting} onClick={handleSubmit}>
            Create Workflow
          </Button>
        )}
      </div>
    </div>
  );
}
